import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { toNumber } from '@theme/utilities';

/**
 * @typedef {object} SubscriptionFormV5Refs
 * @property {HTMLInputElement[]} sellingPlanInputs - Selling plan radio inputs of every option.
 * @property {HTMLInputElement} [discountCodeInput] - Hidden discount code input.
 * @property {HTMLTemplateElement} [optionExtraContent] - Extra content cloned into every subscription option.
 * @extends Component<SubscriptionFormV5Refs>
 */

/**
 * Subscription form v5.
 *
 * Renders nothing but the list of options: the add to cart is handled by the
 * buy-buttons block, so every input of this block is associated with the
 * `BuyButtons-ProductForm-<section id>` form through the `form` attribute.
 */
class SubscriptionFormV5 extends Component {
  requiredRefs = ['sellingPlanInputs'];

  /** @type {string} */
  static SUBSCRIPTION_PLAN = 'subscription';

  /** @type {string} */
  static ONETIME_PLAN = 'onetime';

  connectedCallback() {
    super.connectedCallback();

    this.parent = this.closest('[data-product]');
    this.productForm =
      (this.dataset.productFormId && document.getElementById(this.dataset.productFormId)) ||
      this.parent?.querySelector('.shopify-product-form') ||
      null;

    this.price = toNumber(this.dataset.price, 0);
    this.servings = toNumber(this.dataset.servings, 0);
    this.giftsPrice = toNumber(this.dataset.giftsPrice, 0);
    this.isSubscriptionGift = this.dataset.isSubscriptionGift === 'true';
    this.showButtonComparePrice = this.dataset.showButtonComparePrice === 'true';
    this.currency = this.dataset.currency || 'USD';
    this.moneyFormat = this.dataset.moneyFormat || '';
    // Fallback only — the locale is pinned so the output never depends on the browser language.
    this.priceFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
    });

    this.quantitySelector = this.parent?.querySelector('quantity-selector-component') ?? null;
    this.addToCartTextWrapper = this.parent?.querySelector('.add-to-cart-text__wrapper') ?? null;
    this.addToCartBtnText = document.querySelectorAll('[data-add-to-cart-text]');
    this.productInfoPrices = this.parent?.querySelector('.product-info__prices') ?? null;
    this.productInfoServings = this.parent?.querySelector('.product-info__servings-percent span') ?? null;
    this.discountLabels = this.parent?.querySelectorAll('[data-discount-target]') ?? null;
    this.servingsLabels = this.parent?.querySelectorAll('[data-servings-target]') ?? null;
    this.badgeSlider = this.parent?.querySelector('.badge-slider') ?? null;
    this.subscribeGiftFields = this.querySelectorAll('[data-subscribe-gift-field], [data-offer-gift-field]');

    this.currentQuantity = 1;
    this.boundOnQuantityUpdate = this.#onQuantityUpdate.bind(this);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.boundOnQuantityUpdate);

    this.#selectDefaultOption();
    this.#renderOptionExtraContent();
    this.#initHiddenQuantityInput();
    this.#initButtonPrice();
    this.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.boundOnQuantityUpdate);
  }

  /**
   * Radio change handler, wired declaratively in the option markup.
   */
  handleOptionChange() {
    this.update();
  }

  /**
   * Pushes the state of the selected option to the product form and to the
   * neighbouring blocks (add to cart button, gallery, badge slider, labels).
   */
  update() {
    const option = this.selectedOption;
    const isSubscription = this.isSubscription;

    this.#syncHiddenQuantityInput();
    this.#updateDiscountCode(option, isSubscription);
    this.#updateGiftFields(isSubscription);
    this.#updateVisibility(isSubscription);
    this.#updateAddToCartText(isSubscription);
    this.#updateProductTotalPriceBlocks();
    this.#updateProductInfo();
    this.#updateDiscountLabels();
    this.#updateServingsLabels();
    this.#updateBadgeSlider();
    this.#updateFirstImage(option);
  }

  /**
   * Falls back to the first option when none of them is selected in the theme editor,
   * so the form never submits without a selling plan by accident.
   */
  #selectDefaultOption() {
    const inputs = this.refs.sellingPlanInputs;

    if (!inputs?.length || inputs.some((input) => input.checked)) return;

    inputs[0].checked = true;
  }

  /**
   * The extra content is authored on the parent block, but has to appear in the details of
   * every subscription option — a child block cannot read the settings of its parent, so the
   * markup is cloned from a template instead.
   */
  #renderOptionExtraContent() {
    const template = this.refs.optionExtraContent;
    if (!(template instanceof HTMLTemplateElement)) return;

    for (const option of this.querySelectorAll('.sub-form-v5__option[data-plan="subscription"]')) {
      const details = option.querySelector('.sub-form-v5__details');
      if (!details) continue;

      const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));

      for (const node of Array.from(content.children)) {
        node.classList.add('sub-form-v5__detail', 'sub-form-v5__detail--extra');
      }

      details.appendChild(content);
    }
  }

  // GETTERS

  /**
   * @returns {HTMLElement | null} The label of the checked option.
   */
  get selectedOption() {
    const checked = this.refs.sellingPlanInputs?.find((input) => input.checked);

    return checked?.closest('.sub-form-v5__option') ?? null;
  }

  /**
   * @returns {boolean}
   */
  get isSubscription() {
    return this.selectedOption?.dataset.plan !== SubscriptionFormV5.ONETIME_PLAN;
  }

  /**
   * Settings of the selected option, with fallbacks for the case when nothing
   * is selected yet.
   */
  get selectedOptionConfig() {
    const option = this.selectedOption;

    return {
      count: toNumber(option?.dataset.count, 1),
      discountPercentage: toNumber(option?.dataset.discountPercentage, 0),
      discountCode: option?.dataset.discountCode || '',
    };
  }

  // PRICE CALCULATIONS

  /**
   * Price of a single unit with the discount of the selected option applied.
   * @returns {number} price in cents
   */
  get discountedPrice() {
    const { discountPercentage } = this.selectedOptionConfig;

    if (!this.price || discountPercentage <= 0) {
      return this.price;
    }

    return Math.round(this.price * ((100 - discountPercentage) / 100));
  }

  /**
   * @returns {number} price in cents of the whole line (unit price × quantity)
   */
  get totalPrice() {
    const productPrice = this.discountedPrice * this.currentQuantity;

    if (this.isSubscription && !this.isSubscriptionGift) {
      return productPrice + this.giftsPrice;
    }

    return productPrice;
  }

  /**
   * Same line total, but before the discount of the selected option — this is
   * what gets struck through next to the total.
   * @returns {number} price in cents
   */
  get comparePrice() {
    const productPrice = this.price * this.currentQuantity;

    if (this.isSubscription && !this.isSubscriptionGift) {
      return productPrice + this.giftsPrice;
    }

    return productPrice;
  }

  /**
   * Formats a price with the money format of the shop, so the JS output matches
   * the prices rendered by the `money_without_trailing_zeros` filter in Liquid.
   * @param {number} cents
   * @returns {string}
   */
  formatPrice(cents) {
    const amount = Math.round(cents);

    if (!this.moneyFormat) {
      return this.priceFormatter.format(amount / 100);
    }

    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = this.moneyFormat.match(placeholder);
    if (!match) return this.moneyFormat;

    /**
     * @param {number} precision
     * @param {string} [thousands]
     * @param {string} [decimal]
     */
    const withDelimiters = (precision, thousands = ',', decimal = '.') => {
      const [integerPart, decimalPart] = (amount / 100).toFixed(precision).split('.');

      return (
        integerPart.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) +
        (decimalPart ? decimal + decimalPart : '')
      );
    };

    let value;

    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(2, "'");
        break;
      default:
        value = withDelimiters(2);
    }

    return this.moneyFormat.replace(placeholder, value);
  }

  // QUANTITY

  /**
   * Creates the hidden `quantity` input that carries the multiplied quantity
   * (base quantity × option count) and renames the visible one so the hidden
   * input wins on submit — this is what accelerated checkout reads as well.
   */
  #initHiddenQuantityInput() {
    if (!this.productForm) return;

    const visibleInput = /** @type {HTMLInputElement | null} */ (
      this.productForm.querySelector('input[name="quantity"]')
    );

    if (visibleInput) {
      visibleInput.setAttribute('name', 'display_quantity');
    }

    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = 'quantity';
    this.visibleQuantityInput = visibleInput;
    this.hiddenQuantityInput = hiddenInput;
    hiddenInput.value = this.#getMultipliedQuantity().toString();
    this.currentQuantity = toNumber(hiddenInput.value, 1);
    this.productForm.appendChild(hiddenInput);
  }

  /**
   * @param {string} [baseQty]
   * @returns {number}
   */
  #getMultipliedQuantity(baseQty) {
    const { count } = this.selectedOptionConfig;
    const quantity = toNumber(baseQty ?? this.visibleQuantityInput?.value, 1) || 1;

    return quantity * count;
  }

  #syncHiddenQuantityInput() {
    this.currentQuantity = this.#getMultipliedQuantity();

    if (this.hiddenQuantityInput) {
      this.hiddenQuantityInput.value = this.currentQuantity.toString();
    }
  }

  #onQuantityUpdate() {
    this.#syncHiddenQuantityInput();
    this.#updateProductTotalPriceBlocks();
    this.#updateProductInfo();
    this.#updateServingsLabels();
  }

  // UI UPDATES

  /**
   * @param {HTMLElement | null} option
   * @param {boolean} isSubscription
   */
  #updateDiscountCode(option, isSubscription) {
    const input = this.refs.discountCodeInput;
    if (!input) return;

    const fallback = isSubscription
      ? this.dataset.defaultSubscriptionDiscountCode
      : this.dataset.defaultOnetimeDiscountCode;

    input.value = option?.dataset.discountCode || fallback || '';
  }

  /**
   * Gift line items are only submitted together with a subscription.
   * @param {boolean} isSubscription
   */
  #updateGiftFields(isSubscription) {
    this.subscribeGiftFields?.forEach((field) => {
      if (isSubscription) {
        field.removeAttribute('disabled');
      } else {
        field.setAttribute('disabled', 'disabled');
      }
    });
  }

  /**
   * @param {boolean} isSubscription
   */
  #updateVisibility(isSubscription) {
    this.parent?.querySelectorAll('[data-visibility="subscription"]').forEach((block) => {
      block.classList.toggle('hidden', !isSubscription);
    });

    this.parent?.querySelectorAll('[data-visibility="onetime"]').forEach((block) => {
      block.classList.toggle('hidden', isSubscription);
    });
  }

  /**
   * @param {boolean} isSubscription
   */
  #updateAddToCartText(isSubscription) {
    const text = isSubscription ? this.dataset.subscriptionCta : this.dataset.onetimeCta;
    if (!text) return;

    this.addToCartBtnText?.forEach((btn) => {
      btn.textContent = text;
    });
  }

  /**
   * Appends the total price to the add to cart button label, the same way v1–v3 do.
   */
  #initButtonPrice() {
    if (!this.addToCartTextWrapper) return;

    const priceSpan = document.createElement('span');
    priceSpan.className = 'add-to-cart-text__price';

    const totalPriceSpan = document.createElement('span');
    totalPriceSpan.dataset.productTotalPrice = '';
    priceSpan.appendChild(totalPriceSpan);

    if (this.showButtonComparePrice) {
      const comparePriceSpan = document.createElement('span');
      comparePriceSpan.className = 'add-to-cart-text__price-old';
      comparePriceSpan.dataset.productComparePrice = '';
      comparePriceSpan.hidden = true;
      priceSpan.appendChild(comparePriceSpan);
    }

    this.addToCartTextWrapper.appendChild(priceSpan);
    this.productTotalPriceBlocks = this.parent?.querySelectorAll('[data-product-total-price]') ?? null;
    this.productComparePriceBlocks = this.parent?.querySelectorAll('[data-product-compare-price]') ?? null;
  }

  #updateProductTotalPriceBlocks() {
    const price = this.totalPrice;
    const comparePrice = this.comparePrice;
    const showCompare = this.showButtonComparePrice && comparePrice > price;

    this.productTotalPriceBlocks?.forEach((block) => {
      block.textContent = this.formatPrice(price);
      block.parentElement?.classList.toggle('hidden', price === 0);
    });

    this.productComparePriceBlocks?.forEach((block) => {
      block.textContent = this.formatPrice(comparePrice);
      block.toggleAttribute('hidden', !showCompare);
    });
  }

  #updateProductInfo() {
    const discountedPrice = this.discountedPrice;

    if (this.productInfoServings && this.servings > 0) {
      this.productInfoServings.textContent = this.formatPrice(discountedPrice / this.servings);
    }

    if (!this.productInfoPrices) return;

    const finalPrice = discountedPrice * this.currentQuantity;
    const fullPrice = this.price * this.currentQuantity;
    const oldEl = this.productInfoPrices.querySelector('[class*="--old"]');
    const currentEl = this.productInfoPrices.querySelector('[class*="--current"]');

    if (oldEl) oldEl.textContent = this.formatPrice(fullPrice);
    if (currentEl) currentEl.textContent = this.formatPrice(finalPrice);

    this.productInfoPrices.classList.toggle('hide-old-price', finalPrice === fullPrice);
  }

  #updateDiscountLabels() {
    const { discountPercentage } = this.selectedOptionConfig;

    this.discountLabels?.forEach((el) => {
      if (el instanceof HTMLInputElement) {
        el.value = discountPercentage.toString();
      } else {
        el.textContent = discountPercentage.toString();
        el.parentElement?.classList.toggle('hidden', discountPercentage === 0);
      }
    });
  }

  #updateServingsLabels() {
    if (this.servings <= 0) return;

    const finalServings = this.servings * this.currentQuantity;

    this.servingsLabels?.forEach((el) => {
      el.textContent = finalServings.toString();
    });
  }

  #updateBadgeSlider() {
    if (!this.badgeSlider) return;

    const discountedPrice = this.discountedPrice;
    const priceBlock = this.badgeSlider.querySelector('.badge-slider__price');
    const perServingBlock = this.badgeSlider.querySelector('[data-badge-slider-per-serving]');

    if (priceBlock) {
      priceBlock.textContent = this.formatPrice(discountedPrice);
    }

    if (perServingBlock && this.servings > 0) {
      perServingBlock.textContent = this.formatPrice(discountedPrice / this.servings);
    }
  }

  /**
   * @param {HTMLElement | null} option
   */
  #updateFirstImage(option) {
    const firstImageUrl = option?.dataset.firstImage;
    if (!firstImageUrl || !this.parent) return;

    const img = this.parent.querySelector('[data-first-slide-image] img');
    if (!img) return;

    img.src = firstImageUrl;
    img.srcset = firstImageUrl;
    img.setAttribute('data_max_resolution', firstImageUrl);

    const thumbnailImg = this.parent.querySelector('[data-first-thumbnail-image] img');

    if (thumbnailImg) {
      thumbnailImg.src = firstImageUrl;
      thumbnailImg.srcset = firstImageUrl;
    }
  }
}

if (!customElements.get('subscription-form-v5')) {
  customElements.define('subscription-form-v5', SubscriptionFormV5);
}
